import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import { RootState } from "../../../../globals";
import HttpManager from "../../../../services/HttpManager";
import { DeMeta, Plantilla, PlantillasCatalogos } from "../../../../services/v2/plantillas.service";
import PlantillaEditor from "./plantillaEditor";
import VistaPrevia from "./vistaPrevia";
import "./plantillas.css";

const errorTexto = (e: any): string => e?.response?.data?.message || e?.message || "Algo falló. Intenta otra vez.";

type Pestana = "todas" | "por_aprobar" | "aprobada" | "borrador" | "rechazada" | "archivada" | "meta";
const PESTANAS: { k: Pestana; t: string }[] = [
	{ k: "todas", t: "Todas" }, { k: "por_aprobar", t: "Por aprobar" }, { k: "aprobada", t: "Aprobadas" },
	{ k: "borrador", t: "Borradores" }, { k: "rechazada", t: "Por corregir" }, { k: "archivada", t: "Archivadas" },
];

/** Resumen del estado en Meta para la tarjeta: "Aprobada en 5 de 7 números". */
const resumenMeta = (p: Plantilla): { texto: string; clase: string } | null => {
	if (p.estado !== "aprobada" || !p.numeros.length) return null;
	const n = p.numeros.length;
	const ok = p.numeros.filter((x) => x.estado === "APPROVED").length;
	const mal = p.numeros.filter((x) => ["REJECTED", "ERROR", "PAUSED", "DISABLED"].includes(x.estado)).length;
	if (ok === n) return { texto: n === 1 ? "Lista para usar" : "Lista en los " + n + " números", clase: "ok" };
	if (mal) return { texto: mal + " de " + n + " con problema en Meta", clase: "mal" };
	return { texto: ok ? "Lista en " + ok + " de " + n + "; el resto en revisión de Meta" : "En revisión de Meta", clase: "espera" };
};

/**
 * Plantillas de WhatsApp (menú Mensajería, Esteban 2026-10-05): por departamento, las asesoras sugieren, la supervisora
 * aprueba y WAPP las crea en Meta en todos los números del territorio.
 */
const PlantillasController: React.FC = () => {
	const http = HttpManager.getInstance();
	const sessionCampus: any = useSelector((state: RootState) => state.rootReducer.sessionState.currentCampus);
	const legacyCampusKey: any = useSelector((state: RootState) => state.currentUserCampus.value);
	const legacyCampusRef: any = useSelector((state: RootState) => state.campusRef.value);
	const campusId: number | null = (legacyCampusKey && legacyCampusRef ? legacyCampusRef[legacyCampusKey]?.id : null) ?? sessionCampus?.id ?? null;

	const [cat, setCat] = useState<PlantillasCatalogos | null>(null);
	const [dept, setDept] = useState<string>("");
	const [territorio, setTerritorio] = useState<string>("");
	const [pestana, setPestana] = useState<Pestana>("todas");
	const [buscar, setBuscar] = useState<string>("");
	const [lista, setLista] = useState<Plantilla[] | null>(null);
	const [pendientes, setPendientes] = useState<number>(0);
	const [deMeta, setDeMeta] = useState<DeMeta[] | null>(null);
	const [abierta, setAbierta] = useState<Plantilla | null | "nueva">(null);
	const [error, setError] = useState<string | null>(null);
	const [aviso, setAviso] = useState<string | null>(null);
	const [tick, setTick] = useState<number>(0);
	const [adoptando, setAdoptando] = useState<string | null>(null);

	useEffect(() => {
		if (!campusId) return;
		http.plantillasService.catalogos(campusId).then((r) => {
			setCat(r.data);
			setDept((d) => d || r.data.departamentos[0]?.clave || "");
		}).catch((e) => setError(errorTexto(e)));
	}, [campusId]);

	useEffect(() => {
		if (!campusId || !cat || pestana === "meta") return;
		setLista(null);
		http.plantillasService.lista(campusId, { dept: dept || undefined, territorio: territorio || undefined,
			estado: pestana === "todas" ? undefined : pestana, q: buscar.trim() || undefined })
			.then((r) => { setLista(r.data.data); setPendientes(r.data.pendientesMias); }).catch((e) => setError(errorTexto(e)));
	}, [campusId, cat, dept, territorio, pestana, buscar, tick]);

	useEffect(() => {
		if (!campusId || pestana !== "meta" || !dept) return;
		setDeMeta(null);
		http.plantillasService.deMeta(campusId, dept, territorio || null).then((r) => setDeMeta(r.data)).catch((e) => { setDeMeta([]); setError(errorTexto(e)); });
	}, [campusId, pestana, dept, territorio, tick]);

	// Enlace del aviso de Cliq: /plantillas?id=12 abre esa plantilla.
	useEffect(() => {
		if (!campusId) return;
		const id = Number(new URLSearchParams(window.location.search).get("id") || 0);
		if (!id) return;
		http.plantillasService.detalle(campusId, id).then((r) => setAbierta(r.data)).catch((e) => setError(errorTexto(e)));
		window.history.replaceState(null, "", window.location.pathname);
	}, [campusId]);

	const supervisaAlgo = useMemo(() => !!cat && Object.values(cat.alcance).some((a) => a.supervisa.length > 0), [cat]);
	const supervisaDept = !!cat && !!dept && (cat.alcance[dept]?.supervisa.length || 0) > 0;
	const valores = useMemo(() => Object.fromEntries((cat?.variables || []).map((v) => [v.clave, v.ejemplo])), [cat]);

	const alCambio = (p: Plantilla) => {
		setTick((t) => t + 1);
		const desarchivada = abierta && abierta !== "nueva" && abierta.id === p.id && abierta.estado === "archivada" && p.estado !== "archivada";
		setAviso(desarchivada ? "Sacada de archivadas: ya está en " + p.estado_nombre + "." : p.estado === "por_aprobar" ? "Mandada a tu supervisora para aprobar."
			: p.estado === "aprobada" ? "Aprobada: se mandó a revisión de Meta. Te avisamos cuando la acepte." : p.estado === "archivada" ? "Archivada." : "Guardada.");
		if (abierta && abierta !== "nueva" && abierta.id === p.id) setAbierta(p);
	};

	const adoptar = async (t: DeMeta) => {
		if (!campusId) return;
		setAdoptando(t.meta_name); setError(null);
		try {
			const r = await http.plantillasService.adoptar(campusId, dept, territorio || null, t.meta_name, t.idioma);
			setAviso("Ahora la maneja WAPP: decide quién la puede usar.");
			setTick((x) => x + 1);
			setAbierta(r.data);
		} catch (e) { setError(errorTexto(e)); }
		finally { setAdoptando(null); }
	};

	if (!campusId) return <React.Fragment />;

	return (
		<div className="pl">
			<div className="pl-cab">
				<h1>Plantillas</h1>
				<button className="pl-btn pri" disabled={!cat || !cat.departamentos.length} onClick={() => setAbierta("nueva")}>
					{supervisaAlgo ? "+ Nueva plantilla" : "+ Sugerir plantilla"}
				</button>
			</div>
			{error ? <div className="pl-aviso oro" onClick={() => setError(null)}>{error}</div> : null}
			{aviso ? <div className="pl-aviso ok" onClick={() => setAviso(null)}>{aviso}</div> : null}
			{cat && !cat.departamentos.length ? <div className="pl-vacio">Tu rol no trabaja con plantillas de WhatsApp.</div> : null}

			{cat && cat.departamentos.length ? (
				<>
					<div className="pl-filtros">
						<div className="pl-deptos" role="tablist">
							{cat.departamentos.map((d) => (
								<button key={d.clave} role="tab" aria-selected={dept === d.clave} className={dept === d.clave ? "on" : ""} onClick={() => setDept(d.clave)}>{d.nombre}</button>
							))}
						</div>
						<select value={territorio} onChange={(e) => setTerritorio(e.target.value)} aria-label="Territorio">
							<option value="">Todos los territorios</option>
							{cat.territorios.map((t) => <option key={t.clave} value={t.clave}>{t.nombre}</option>)}
						</select>
						<input className="pl-buscar" type="text" placeholder="Buscar" value={buscar} onChange={(e) => setBuscar(e.target.value)} />
					</div>
					<div className="pl-pestanas" role="tablist">
						{PESTANAS.map((x) => (
							<button key={x.k} role="tab" aria-selected={pestana === x.k} className={pestana === x.k ? "on" : ""} onClick={() => setPestana(x.k)}>
								{x.t}{x.k === "por_aprobar" && pendientes > 0 ? <span className="pl-globo">{pendientes}</span> : null}
							</button>
						))}
						{supervisaDept ? <button role="tab" aria-selected={pestana === "meta"} className={pestana === "meta" ? "on" : ""} onClick={() => setPestana("meta")}>Ya en Meta</button> : null}
					</div>

					{pestana === "meta" ? (
						deMeta === null ? <div className="loader" /> : deMeta.length === 0 ? <div className="pl-vacio">No hay plantillas de Meta sin manejar en este departamento.</div> : (
							<div className="pl-grid">
								{deMeta.map((t) => (
									<div key={t.meta_name + t.idioma} className="pl-tarjeta">
										<div className="pl-tarjeta-cab"><b>{t.meta_name}</b><span className="pl-estado e-meta">De Meta</span></div>
										<p className="pl-tarjeta-texto">{t.cuerpo}</p>
										<div className="pl-tarjeta-pie">
											<span>{t.cuentas.map((c) => c.numeros.join(", ")).join(" · ")}</span>
											<button className="pl-btn chico" disabled={adoptando === t.meta_name} onClick={() => adoptar(t)}>{adoptando === t.meta_name ? "…" : "Manejar en WAPP"}</button>
										</div>
									</div>
								))}
							</div>
						)
					) : lista === null ? <div className="loader" /> : lista.length === 0 ? (
						<div className="pl-vacio">{pestana === "por_aprobar" ? "Nada por aprobar." : "Todavía no hay plantillas aquí."}</div>
					) : (
						<div className="pl-grid">
							{lista.map((p) => {
								const r = resumenMeta(p);
								return (
									<button key={p.id} className="pl-tarjeta" onClick={() => setAbierta(p)}>
										<div className="pl-tarjeta-cab">
											<b>{p.nombre}</b>
											<span className={"pl-estado e-" + p.estado}>{p.estado_nombre}</span>
										</div>
										<div className="pl-tarjeta-mini">
											<VistaPrevia headerTipo={p.header_tipo} headerTexto={p.header_texto} mediaNombre={p.header_media_nombre}
												cuerpo={p.cuerpo} pie={p.pie} botones={p.botones} valores={valores} />
										</div>
										<div className="pl-tarjeta-pie">
											<span>{p.dept_nombre} · {p.territorio_nombre}{p.creador ? " · " + p.creador.split(" ")[0] : ""}</span>
											{r ? <span className={"pl-meta " + r.clase}>{r.texto}</span> : null}
										</div>
									</button>
								);
							})}
						</div>
					)}
				</>
			) : !cat && !error ? <div className="loader" /> : null}

			{abierta && cat ? (
				<PlantillaEditor campusId={campusId} cat={cat} plantilla={abierta === "nueva" ? null : abierta} deptNueva={dept}
					onClose={() => setAbierta(null)} onCambio={alCambio} />
			) : null}
		</div>
	);
};

export default PlantillasController;
